import type { OverviewControlTopPattern } from './overviewTypes';
import { buyerPatternDisplayLabel } from './overviewBuyerDisplay';
import { displayFollowupMeasurementLabel } from './overviewDisplayLabels';

export const OVERVIEW_SITE_RISK_SUMMARY_TITLE = 'Featured hotspot summary';
export const OVERVIEW_SAFETY_CASE_LIFECYCLE_TITLE = 'Site counters';
export const OVERVIEW_SITE_COUNTERS_INTRO =
  'Independent scoped counters for the operational review queue — not a sequential funnel. Signed Safety Control Scans appear only after a scan is finalized.';
export const OVERVIEW_NEEDS_ATTENTION_TITLE = 'Needs Attention';
export const OVERVIEW_OPEN_SAFETY_REPORTS_LABEL = 'Open Safety Reports';
export const OVERVIEW_REVIEW_RISK_PATTERNS_LABEL = 'View all hotspots';
export const OVERVIEW_REVIEW_RISK_PATTERN_LABEL = 'Review hotspot';
export const OVERVIEW_REVIEW_NEW_ACTIVITY_LABEL = 'Review New Activity';
export const OVERVIEW_OPEN_SAFETY_CASE_LABEL = 'Open Safety Case';
export const OVERVIEW_VERIFIED_OUTCOMES_TITLE = 'Verified Outcomes';
export const OVERVIEW_VERIFIED_OUTCOMES_DESCRIPTION =
  'Across site. Counts Safety Cases with a completed comparable measurement that supports an outcome claim. Demo/waived/not-comparable/not-claimed outcomes are not counted.';
export const OVERVIEW_VERIFIED_OUTCOMES_EMPTY_STATUS = 'No verified outcome claims yet';
export const OVERVIEW_VERIFIED_OUTCOME_LIFECYCLE_TITLE = 'Verified outcomes';
export const OVERVIEW_HOTSPOT_DETAIL_TITLE = 'Recurring hotspots requiring review';
export const OVERVIEW_CLASSIFICATION_ATTENTION_TITLE = 'Configuration or classification attention';
export const OVERVIEW_FEATURED_HOTSPOT_TITLE = 'Recurring hotspot requiring review';
export const OVERVIEW_FEATURED_HOTSPOTS_TITLE = 'Recurring hotspots requiring review';
export const OVERVIEW_OBSERVATION_WINDOW_PHRASE = 'the last 7 days';
export const OVERVIEW_ACROSS_SITE_SCOPE_LABEL = 'Across site';
export const OVERVIEW_WINDOW_FLOW_SCOPE_HELPER = `In ${OVERVIEW_OBSERVATION_WINDOW_PHRASE}`;
export const OVERVIEW_HOTSPOTS_REVIEWED_SCOPE_HELPER =
  'Among recurring hotspots in the observation window';
export const OVERVIEW_ACROSS_SITE_STOCK_HELPER = 'Across site · current stock';

const NON_VERIFIED_MEASUREMENT_STATUSES = new Set([
  'waived',
  'pending',
  'not_comparable',
  'insufficient_data',
  'not_claimed',
  'worsened',
  'missing',
  '',
]);

export function isVerifiedOutcomeRow(row: OverviewControlTopPattern): boolean {
  if (!hasSafetyCaseId(row)) return false;
  const measurementStatus = String(row.measurementStatus || '').trim().toLowerCase();
  if (measurementStatus && NON_VERIFIED_MEASUREMENT_STATUSES.has(measurementStatus)) {
    return false;
  }
  const outcomeLabel = displayFollowupMeasurementLabel(row.outcomeMeasurementStatus).trim().toLowerCase();
  if (outcomeLabel !== 'measured') return false;
  const rawOutcome = String(row.outcomeMeasurementStatus || '').trim().toLowerCase();
  if (/waived|pending|insufficient|not_comparable|not_claimed|not measured|worsened/.test(rawOutcome)) {
    return false;
  }
  return true;
}

export function countVerifiedOutcomes(patterns: OverviewControlTopPattern[]): number {
  return patterns.filter(isVerifiedOutcomeRow).length;
}

export function riskPatternsNeedReviewHeadline(count: number): string {
  if (count <= 0) return 'Recurring hotspots appear controlled in this window';
  if (count === 1) return '1 recurring hotspot needs review';
  return `${count.toLocaleString()} recurring hotspots need review`;
}

export function safetyCasesWithNewActivityHeadline(count: number): string {
  if (count <= 0) return 'No Safety Cases with new activity';
  if (count === 1) return '1 Safety Case has new activity';
  return `${count.toLocaleString()} Safety Cases have new activity`;
}

export function safetyCaseNewActivityDetail(
  row: Pick<OverviewControlTopPattern, 'patternLabel' | 'incidentType' | 'safetyCaseId' | 'findingId'> | null,
): string {
  if (!row) return 'Loading Safety Cases and new-activity status for this site.';
  const caseId = resolveSafetyCaseId(row);
  const patternLabel = buyerPatternDisplayLabel(row);
  if (caseId) {
    return `${patternLabel} has new observations after the latest Signed Safety Scan. Linked Safety Case is active.`;
  }
  return `${patternLabel} has new observations after the latest Signed Safety Scan.`;
}

export function riskPatternActivityDetail(
  row: Pick<OverviewControlTopPattern, 'patternLabel' | 'incidentType'> | null,
  _windowPhrase: string,
): string {
  if (!row) return 'Loading recurring hotspots and review status for this site.';
  return `${buyerPatternDisplayLabel(row)} is in the operational review queue. Review the pattern before creating a Safety Case or Safety Control Scan.`;
}

export function riskPatternsNeedReviewKpiTitle(count: number): string {
  if (count === 1) return 'Recurring hotspot requiring review';
  return 'Recurring hotspots requiring review';
}

export function viewAllHotspotsLabel(count: number): string {
  const n = Math.max(0, Number(count) || 0);
  if (n <= 1) return 'View all hotspots';
  return `View all ${n.toLocaleString()} hotspots`;
}

export function qualifyingEntriesInWindowLabel(count: number, windowPhrase: string = OVERVIEW_OBSERVATION_WINDOW_PHRASE): string {
  const n = Math.max(0, Number(count) || 0);
  const noun = n === 1 ? 'qualifying entry' : 'qualifying entries';
  return `${n.toLocaleString()} ${noun} in ${windowPhrase}`;
}

export function activeSafetyCasesKpiTitle(count: number): string {
  if (count === 1) return 'Active Safety Case';
  return 'Active Safety Cases';
}

function parseTimestamp(value?: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function resolveSafetyCaseId(
  row: Pick<OverviewControlTopPattern, 'safetyCaseId' | 'findingId'> | null | undefined,
): string {
  return String(row?.safetyCaseId || row?.findingId || '').trim();
}

export function hasSafetyCaseId(
  row: Pick<OverviewControlTopPattern, 'safetyCaseId' | 'findingId'> | null | undefined,
): boolean {
  return Boolean(resolveSafetyCaseId(row));
}

export function isSignedSafetyScanRow(
  row: Pick<OverviewControlTopPattern, 'reportStatus' | 'signoffStatus' | 'latestSignedScanAt' | 'latestSignedScanId'>,
): boolean {
  const reportStatus = String(row.reportStatus || '').trim().toLowerCase();
  const signoffStatus = String(row.signoffStatus || '').trim().toLowerCase();
  return reportStatus === 'finalized'
    || signoffStatus === 'completed'
    || Boolean(row.latestSignedScanAt)
    || Boolean(row.latestSignedScanId);
}

export function isSafetyCaseWithNewActivity(row: OverviewControlTopPattern): boolean {
  if (!hasSafetyCaseId(row) || !isSignedSafetyScanRow(row)) return false;
  if (row.hasNewActivitySinceLatestSignedScan != null) {
    return Boolean(row.hasNewActivitySinceLatestSignedScan);
  }
  const latestObservationAt = parseTimestamp(row.latestObservationAt);
  const latestSignedScanAt = parseTimestamp(row.latestSignedScanAt);
  if (latestObservationAt == null || latestSignedScanAt == null) return false;
  return latestObservationAt > latestSignedScanAt;
}

export function countActiveSafetyCases(patterns: OverviewControlTopPattern[]): number {
  return patterns.filter((row) => hasSafetyCaseId(row)).length;
}

export function countSafetyCasesWithNewActivity(patterns: OverviewControlTopPattern[]): number {
  return patterns.filter(isSafetyCaseWithNewActivity).length;
}

export function countReviewedRiskPatterns(patterns: OverviewControlTopPattern[]): number {
  return patterns.filter((row) => String(row.reviewStatus || '').trim().toLowerCase() === 'reviewed').length;
}

const UNASSIGNED_FOLLOWUP_OWNER = new Set(['', 'not assigned', 'unassigned', 'none', 'null', 'n/a']);

export function hasAssignedFollowupOwner(
  row: Pick<OverviewControlTopPattern, 'safetyCaseId' | 'findingId' | 'followupOwner'> | null | undefined,
): boolean {
  if (!hasSafetyCaseId(row)) return false;
  const owner = String(row?.followupOwner || '').trim().toLowerCase();
  return Boolean(owner) && !UNASSIGNED_FOLLOWUP_OWNER.has(owner);
}

export function countSafetyCasesWithAssignedActions(patterns: OverviewControlTopPattern[]): number {
  return patterns.filter(hasAssignedFollowupOwner).length;
}

export function countSafetyCasesWithMeasuredOutcomes(patterns: OverviewControlTopPattern[]): number {
  return countVerifiedOutcomes(patterns);
}

export function reviewStateLabel(status?: string | null): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'reviewed') return 'Reviewed';
  if (normalized === 'pending') return 'Under review';
  return 'Needs review';
}

export function caseStateLabel(row: OverviewControlTopPattern | null): string {
  if (!row) return 'Not created yet';
  if (!row.findingSaved && !hasSafetyCaseId(row)) return 'Not created yet';
  if (isSignedSafetyScanRow(row)) return 'Active';
  return 'In progress';
}

export function measurementStateLabel(row: OverviewControlTopPattern | null): string {
  return displayFollowupMeasurementLabel(row?.outcomeMeasurementStatus);
}

export function overviewAttentionKind(
  row: OverviewControlTopPattern | null,
): 'risk_pattern' | 'safety_case_new_activity' {
  if (row && isSafetyCaseWithNewActivity(row)) return 'safety_case_new_activity';
  return 'risk_pattern';
}
